/**
 * Hours of Rest Regulations Engine
 * Defines compliance thresholds and checking logic
 * Citations as strings only (no PDF imports)
 *
 * STCW Rules Implemented:
 * - Rule 1: Minimum 10h rest in any 24-hour period
 * - Rule 2: Minimum 77h rest in any 7-day period (rolling window)
 * - Rule 3: Rest may be divided into max 2 periods (one >= 6h, other >= 1h, max 14h between)
 * - Rule 4: Maximum 14h work in any 24-hour period
 * - Rule 5: Maximum 72h work in any 7-day period
 */

export const hoursOfRestRules = () => ({
  meta: {
    id: 'hours_of_rest_baseline',
    title: 'Hours of Rest Minimums',
    citations: [
      'UK Workboat Code Edition 3, Section 10.3',
      'MGN 505 - Hours of Work and Manning',
      'ILO Maritime Labour Convention 2006'
    ]
  },

  thresholds: {
    minDailyRest: 10,   // Rule 1: Minimum hours of rest in any 24-hour period
    minWeeklyRest: 77,  // Rule 2: Minimum hours of rest in any 7-day period
    maxWorkDaily: 14,   // Rule 4: Maximum work hours in any 24-hour period
    maxWorkWeekly: 72,  // Rule 5: Maximum work hours in any 7-day period
    minLongRestPeriod: 6,  // Rule 3: One rest period must be >= 6 hours
    minShortRestPeriod: 1, // Rule 3: Other rest period must be >= 1 hour
    maxRestPeriods: 2,     // Rule 3: Rest can be divided into max 2 periods
    maxHoursBetweenRest: 14 // Rule 3: Maximum 14 hours between consecutive rest periods
  },

  checkMonth(monthKey, records) {
    // Group records by crew member
    const byCrewId = {};
    for (const record of records) {
      const crewId = record.crewId || 'unknown';
      (byCrewId[crewId] ||= []).push(record);
    }

    // Check compliance for each crew member
    let dailyBreaches = 0;
    let weeklyBreaches = 0;
    let restSplitBreaches = 0;
    const details = [];

    for (const [crewId, crewRecords] of Object.entries(byCrewId)) {
      // Sort by date for sequential processing
      const sorted = crewRecords.sort((a, b) =>
        new Date(a.date).getTime() - new Date(b.date).getTime()
      );

      // Check daily minimums (Rule 1) and max work (Rule 4)
      for (const record of sorted) {
        // Rule 1: Check minimum daily rest
        if (record.restHours < this.thresholds.minDailyRest) {
          dailyBreaches++;
          details.push({
            type: 'daily_rest',
            rule: 1,
            crewId,
            date: record.date,
            restHours: record.restHours,
            required: this.thresholds.minDailyRest
          });
        }

        // Rule 4: Check maximum daily work
        const workHours = record.workHours || (24 - (record.restHours || 0));
        if (workHours > this.thresholds.maxWorkDaily) {
          dailyBreaches++;
          details.push({
            type: 'daily_work',
            rule: 4,
            crewId,
            date: record.date,
            workHours,
            maxAllowed: this.thresholds.maxWorkDaily
          });
        }

        // Rule 3: Check rest period split
        const restSplitCheck = this.checkRestSplit(record);
        if (!restSplitCheck.compliant) {
          restSplitBreaches++;
          details.push({
            type: 'rest_split',
            rule: 3,
            crewId,
            date: record.date,
            ...restSplitCheck
          });
        }
      }

      // Rule 2 & 5: Check 7-day rolling window for weekly rest and work limits
      const weeklyCheck = this.check7DayRollingWindow(sorted, crewId);
      weeklyBreaches += weeklyCheck.breaches.length;
      details.push(...weeklyCheck.breaches);
    }

    return {
      dailyBreaches,
      weeklyBreaches,
      restSplitBreaches,
      details,
      compliant: dailyBreaches === 0 && weeklyBreaches === 0 && restSplitBreaches === 0,
      summary: {
        totalRecords: records.length,
        crewCount: Object.keys(byCrewId).length,
        monthKey
      }
    };
  },

  /**
   * Rule 2 & 5: Check 7-day rolling window compliance
   * For each day, look at the 7-day window ending on that day
   * Returns breaches for both weekly rest (Rule 2) and weekly work (Rule 5)
   * FT-156: Missing days assumed as 24h rest, 0h work
   */
  check7DayRollingWindow(sortedRecords, crewId) {
    const breaches = [];
    if (sortedRecords.length < 7) return { breaches, windows: [] };

    const windows = [];

    // For each record, calculate the 7-day window ending on that date
    for (let i = 6; i < sortedRecords.length; i++) {
      const windowEnd = new Date(sortedRecords[i].date);
      const windowStart = new Date(windowEnd);
      windowStart.setDate(windowStart.getDate() - 6);

      // Get all records in this 7-day window
      const windowRecords = sortedRecords.filter(r => {
        const recordDate = new Date(r.date);
        return recordDate >= windowStart && recordDate <= windowEnd;
      });

      // FT-156: Create a set of recorded dates to identify missing days
      const recordedDates = new Set(windowRecords.map(r => r.date));

      // Sum rest and work hours in the window, accounting for missing days
      let totalRest = 0;
      let totalWork = 0;

      // Iterate through each day in the window
      const currentDate = new Date(windowStart);
      while (currentDate <= windowEnd) {
        const dateStr = currentDate.toISOString().split('T')[0];

        if (recordedDates.has(dateStr)) {
          const record = windowRecords.find(r => r.date === dateStr);
          totalRest += record.restHours || 0;
          totalWork += record.workHours || (24 - (record.restHours || 0));
        } else {
          // FT-156: Missing day = assume 24h rest, 0 work
          totalRest += 24;
          // totalWork += 0; (implicit)
        }

        currentDate.setDate(currentDate.getDate() + 1);
      }

      const windowInfo = {
        startDate: windowStart.toISOString().split('T')[0],
        endDate: sortedRecords[i].date,
        totalRest,
        totalWork,
        recordCount: windowRecords.length
      };
      windows.push(windowInfo);

      // Rule 2: Check weekly rest minimum (77h)
      if (totalRest < this.thresholds.minWeeklyRest) {
        breaches.push({
          type: 'weekly_rest',
          rule: 2,
          crewId,
          date: sortedRecords[i].date,
          windowStart: windowInfo.startDate,
          restHours: totalRest,
          required: this.thresholds.minWeeklyRest
        });
      }

      // Rule 5: Check weekly work maximum (72h)
      if (totalWork > this.thresholds.maxWorkWeekly) {
        breaches.push({
          type: 'weekly_work',
          rule: 5,
          crewId,
          date: sortedRecords[i].date,
          windowStart: windowInfo.startDate,
          workHours: totalWork,
          maxAllowed: this.thresholds.maxWorkWeekly
        });
      }
    }

    return { breaches, windows };
  },

  /**
   * Rule 3: Check rest period split compliance
   * Rest may be divided into max 2 periods, one >= 6h, other >= 1h
   * Maximum 14 hours between consecutive rest periods
   */
  checkRestSplit(record) {
    // If no rest periods data, assume single period (compliant)
    if (!record.restPeriods || !Array.isArray(record.restPeriods)) {
      return { compliant: true, reason: 'single_period_assumed' };
    }

    const periods = record.restPeriods;

    // Check number of periods (max 2)
    if (periods.length > this.thresholds.maxRestPeriods) {
      return {
        compliant: false,
        reason: 'too_many_periods',
        periodCount: periods.length,
        maxAllowed: this.thresholds.maxRestPeriods
      };
    }

    // If only one period, it's compliant (must be >= 10h total, checked elsewhere)
    if (periods.length <= 1) {
      return { compliant: true, reason: 'single_period' };
    }

    // Sort periods by duration (longest first)
    const sortedPeriods = [...periods].sort((a, b) =>
      (b.duration || b.hours || 0) - (a.duration || a.hours || 0)
    );

    const longPeriod = sortedPeriods[0].duration || sortedPeriods[0].hours || 0;
    const shortPeriod = sortedPeriods[1].duration || sortedPeriods[1].hours || 0;

    // Check minimum durations
    if (longPeriod < this.thresholds.minLongRestPeriod) {
      return {
        compliant: false,
        reason: 'long_period_too_short',
        longPeriod,
        required: this.thresholds.minLongRestPeriod
      };
    }

    if (shortPeriod < this.thresholds.minShortRestPeriod) {
      return {
        compliant: false,
        reason: 'short_period_too_short',
        shortPeriod,
        required: this.thresholds.minShortRestPeriod
      };
    }

    // Check gap between rest periods (if times are provided)
    if (periods[0].endTime && periods[1].startTime) {
      const gap = this.calculateGapHours(periods[0].endTime, periods[1].startTime);
      if (gap > this.thresholds.maxHoursBetweenRest) {
        return {
          compliant: false,
          reason: 'gap_too_long',
          gapHours: gap,
          maxAllowed: this.thresholds.maxHoursBetweenRest
        };
      }
    }

    return { compliant: true, reason: 'valid_split' };
  },

  /**
   * Calculate hours between two time strings (HH:MM format)
   */
  calculateGapHours(endTime, startTime) {
    const [endH, endM] = endTime.split(':').map(Number);
    const [startH, startM] = startTime.split(':').map(Number);

    let endMinutes = endH * 60 + endM;
    let startMinutes = startH * 60 + startM;

    // Handle overnight gap
    if (startMinutes < endMinutes) {
      startMinutes += 24 * 60;
    }

    return (startMinutes - endMinutes) / 60;
  },

  // Helper to check a single day's compliance (Rules 1, 3, 4)
  checkDay(record) {
    const restHours = record.restHours || 0;
    const workHours = record.workHours || 24 - restHours;
    const restSplit = this.checkRestSplit(record);

    return {
      dailyRestCompliant: restHours >= this.thresholds.minDailyRest,
      dailyWorkCompliant: workHours <= this.thresholds.maxWorkDaily,
      restSplitCompliant: restSplit.compliant,
      restHours,
      workHours,
      restSplitDetails: restSplit
    };
  },

  /**
   * Get compliance for a specific crew member over a date range
   * Useful for crew profile summaries
   */
  checkCrewCompliance(records, crewId, options = {}) {
    const { days = 30 } = options;
    const now = new Date();
    const cutoff = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

    // Filter to crew member and date range
    const crewRecords = records
      .filter(r => r.crewId === crewId && new Date(r.date) >= cutoff)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    if (crewRecords.length === 0) {
      return {
        hasData: false,
        recordCount: 0,
        compliant: null,
        message: 'No hours of rest data recorded'
      };
    }

    // Check all rules
    let dailyBreaches = 0;
    let weeklyBreaches = 0;
    let restSplitBreaches = 0;
    const breachDetails = [];

    // Daily checks (Rules 1, 3, 4)
    for (const record of crewRecords) {
      const dayCheck = this.checkDay(record);

      if (!dayCheck.dailyRestCompliant) {
        dailyBreaches++;
        breachDetails.push({
          rule: 1,
          date: record.date,
          type: 'daily_rest',
          actual: dayCheck.restHours,
          required: this.thresholds.minDailyRest
        });
      }

      if (!dayCheck.dailyWorkCompliant) {
        dailyBreaches++;
        breachDetails.push({
          rule: 4,
          date: record.date,
          type: 'daily_work',
          actual: dayCheck.workHours,
          maxAllowed: this.thresholds.maxWorkDaily
        });
      }

      if (!dayCheck.restSplitCompliant) {
        restSplitBreaches++;
        breachDetails.push({
          rule: 3,
          date: record.date,
          type: 'rest_split',
          ...dayCheck.restSplitDetails
        });
      }
    }

    // Weekly checks (Rules 2, 5)
    const weeklyCheck = this.check7DayRollingWindow(crewRecords, crewId);
    weeklyBreaches = weeklyCheck.breaches.length;
    breachDetails.push(...weeklyCheck.breaches);

    // Calculate averages
    const totalRest = crewRecords.reduce((sum, r) => sum + (r.restHours || 0), 0);
    const totalWork = crewRecords.reduce((sum, r) => sum + (r.workHours || 24 - (r.restHours || 0)), 0);

    return {
      hasData: true,
      recordCount: crewRecords.length,
      dateRange: {
        from: crewRecords[0].date,
        to: crewRecords[crewRecords.length - 1].date
      },
      compliant: dailyBreaches === 0 && weeklyBreaches === 0 && restSplitBreaches === 0,
      breaches: {
        daily: dailyBreaches,
        weekly: weeklyBreaches,
        restSplit: restSplitBreaches,
        total: dailyBreaches + weeklyBreaches + restSplitBreaches
      },
      breachDetails,
      averages: {
        dailyRest: crewRecords.length > 0 ? (totalRest / crewRecords.length).toFixed(1) : 0,
        dailyWork: crewRecords.length > 0 ? (totalWork / crewRecords.length).toFixed(1) : 0
      },
      latestRecord: crewRecords[crewRecords.length - 1]
    };
  },

  // Helper to format breach message
  formatBreach(breach) {
    switch (breach.type) {
      case 'daily_rest':
        return `${breach.crewId} on ${breach.date}: Only ${breach.restHours}h rest (min ${breach.required}h) [Rule 1]`;
      case 'daily_work':
        return `${breach.crewId} on ${breach.date}: ${breach.workHours}h work (max ${breach.maxAllowed}h) [Rule 4]`;
      case 'weekly_rest':
        return `${breach.crewId} week ending ${breach.date}: Only ${breach.restHours}h rest (min ${breach.required}h) [Rule 2]`;
      case 'weekly_work':
        return `${breach.crewId} week ending ${breach.date}: ${breach.workHours}h work (max ${breach.maxAllowed}h) [Rule 5]`;
      case 'rest_split':
        return `${breach.crewId} on ${breach.date}: Invalid rest split - ${breach.reason} [Rule 3]`;
      default:
        return 'Unknown breach type';
    }
  }
});